/**
 * ReviewTimelineCard — "What happened", oldest first (BON-381).
 *
 * The ONE trail on this page. It used to sit beside a separate "Review history"
 * card, but the API assembles `timeline` from every source it has — the booking's
 * action-provenance events, declined-attempt counts, Stripe's status AND the
 * review lifecycle — while `history` carries only the lifecycle. The lifecycle
 * is therefore a strict subset, and on an item whose other sources are empty the
 * two rendered identically. One story, one place.
 *
 * Because the lifecycle entries now live only here, an operator's resolve/reopen
 * note (which arrives as the entry's `detail`) gets its own line in readable ink
 * rather than being crammed into the meta line after the actor — the note is the
 * record of the remedy, and it is often a sentence.
 *
 * The content stays as thin as our data really is: per-decline timestamps do not
 * exist, only a counter, so three declines contribute ONE entry. Whatever the
 * API sends is what renders; nothing is padded to look richer.
 */
import { Circle, Clock } from 'lucide-react';

import { formatShortInstant } from './reviewQueueModel';
import type { ReviewTimelineEntry } from './reviewTypes';

export interface ReviewTimelineCardProps {
  entries: ReviewTimelineEntry[];
}

function TimelineRow({ entry, last }: { entry: ReviewTimelineEntry; last: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: '11px 16px',
        borderBottom: last ? undefined : '1px solid var(--nb-border)',
      }}
    >
      <span
        style={{
          flex: '0 0 26px',
          width: 26,
          height: 26,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: entry.flag ? 'var(--nb-warn-soft)' : 'var(--nb-surface)',
          color: entry.flag ? 'var(--nb-warn)' : 'var(--nb-muted)',
        }}
      >
        {entry.flag ? <Circle size={10} fill="currentColor" /> : <Clock size={13} />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nb-ink)' }}>{entry.label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>{entry.actor}</div>
        {entry.detail && (
          <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--nb-ink-2)', marginTop: 3 }}>
            {entry.detail}
          </div>
        )}
      </div>
      <span
        className="nb-mono"
        style={{ flex: '0 0 auto', fontSize: 11, color: 'var(--nb-faint)' }}
      >
        {formatShortInstant(entry.at)}
      </span>
    </div>
  );
}

export function ReviewTimelineCard({ entries }: ReviewTimelineCardProps) {
  return (
    <section className="nb-card nb-card-tight" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          padding: '12px 16px',
          borderBottom: '1px solid var(--nb-border)',
        }}
      >
        <h3
          className="nb-display"
          style={{ margin: 0, fontSize: 15, color: 'var(--nb-bark-dark)' }}
        >
          What happened
        </h3>
        <span style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>
          {entries.length} {entries.length === 1 ? 'event' : 'events'} · oldest first
        </span>
      </div>
      {entries.length === 0 ? (
        <div style={{ padding: '18px 16px', fontSize: 12.5, color: 'var(--nb-subtle)' }}>
          Nothing else is recorded for this item.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {entries.map((entry, index) => (
            <TimelineRow
              key={`${entry.at}-${entry.label}-${index}`}
              entry={entry}
              last={index === entries.length - 1}
            />
          ))}
        </div>
      )}
    </section>
  );
}
